import React, { useCallback, useMemo, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import NFLProjectionsTable from "./NFLProjectionsTable";
import NFLToolLayout from "../__shared/NFLToolLayout";
import FilterBar from "../__shared/FilterBar";
import PositionFilter from "../__shared/PositionFilter";
import TeamFilter from "../__shared/TeamFilter";
import ToolStatePanel, { TableSkeleton } from "../__shared/ToolStatePanel";
import ToolErrorBoundary from "../__shared/ToolErrorBoundary";
import useNflReference from "../__shared/useNflReference";
import useUrlFilters from "../__shared/useUrlFilters";
import { PROJECTION_POSITIONS } from "./projectionRows";
import { isSiteAdmin, isToolSub } from "../../../../../AuthUtils";
import "./NFLProjections.css";

const FILTER_OPTIONS = { positions: PROJECTION_POSITIONS };
const TITLE = "Projections";
const SUBTITLE = "Gup's weekly DFS projections by position.";
const SKELETON_COLUMNS = [
  "Name",
  "Pos",
  "Team",
  "Opp",
  "Proj Points",
  "Proj Own",
  "DK Salary",
  "DK $ Value",
];

export default function NFLProjectionsPage() {
  const [referenceFetchKey, setReferenceFetchKey] = useState(0);

  return (
    <ToolErrorBoundary
      what="Projections"
      onRetry={() => setReferenceFetchKey((key) => key + 1)}
      wrapError={(panel) => (
        <NFLToolLayout tool="projections" title={TITLE} subtitle={SUBTITLE}>
          {panel}
        </NFLToolLayout>
      )}
    >
      <React.Suspense
        fallback={
          <NFLToolLayout tool="projections" title={TITLE} subtitle={SUBTITLE}>
            <TableSkeleton columns={SKELETON_COLUMNS} />
          </NFLToolLayout>
        }
      >
        <ProjectionsTool referenceFetchKey={referenceFetchKey} />
      </React.Suspense>
    </ToolErrorBoundary>
  );
}

function ProjectionsTool({ referenceFetchKey }) {
  const reference = useNflReference({ fetchKey: referenceFetchKey });
  const filters = useUrlFilters(FILTER_OPTIONS);
  const [fetchKey, setFetchKey] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);

  const teamByAbbrev = useMemo(() => {
    const map = {};
    reference.teams.forEach((team) => {
      map[team.abbreviation] = team;
    });
    return map;
  }, [reference.teams]);

  const knownTeams = useMemo(
    () => filters.teams.filter((abbrev) => teamByAbbrev[abbrev]),
    [filters.teams, teamByAbbrev]
  );

  const retry = useCallback(() => {
    setUpdatedAt(null);
    setFetchKey((key) => key + 1);
  }, []);

  const isAdmin = isSiteAdmin(reference.groups);

  if (!isToolSub(reference.groups)) {
    return <Redirect to="/memberships" />;
  }

  const chips = [
    ...filters.positions.map((pos) => ({
      key: `pos-${pos}`,
      label: pos,
      onRemove: () => filters.togglePosition(pos),
    })),
    ...knownTeams.map((abbrev) => {
      const team = teamByAbbrev[abbrev];
      return {
        key: `team-${abbrev}`,
        label: team.name,
        color: team.primaryColorHex,
        onRemove: () => filters.toggleTeam(abbrev),
      };
    }),
  ];

  const clearAll = chips.length > 0 ? () => filters.clear() : undefined;
  const lastChip = chips[chips.length - 1];

  const currentWeek = reference.currentSeason
    ? reference.currentSeason.currentWeek
    : null;

  const notPostedState = (
    <ToolStatePanel
      badge={currentWeek}
      title={
        currentWeek
          ? `Week ${currentWeek} projections aren't posted yet`
          : "Projections aren't posted yet"
      }
      body={
        isAdmin
          ? "Subscribers see “Projections go up by Thursday morning” until you upload."
          : "Projections go up by Thursday morning at the latest."
      }
      actions={
        isAdmin ? (
          <Link className="nfl-btn nfl-btn-primary" to="/tools/nfl/projections/admin">
            Upload projections
          </Link>
        ) : null
      }
    />
  );

  const emptyState =
    chips.length > 0 ? (
      <ToolStatePanel
        title="No players match these filters"
        body={chips.map((chip) => chip.label).join(" · ")}
        actions={
          <>
            <button type="button" className="nfl-btn" onClick={lastChip.onRemove}>
              Remove {lastChip.label}
            </button>
            <button type="button" className="nfl-btn nfl-btn-ghost" onClick={clearAll}>
              Clear all filters
            </button>
          </>
        }
      />
    ) : (
      notPostedState
    );

  return (
    <NFLToolLayout
      tool="projections"
      title={TITLE}
      subtitle={SUBTITLE}
      updatedAt={updatedAt}
      actions={
        isAdmin ? (
          <Link
            className="nfl-btn nfl-projections-admin-link"
            to="/tools/nfl/projections/admin"
          >
            Upload projections
          </Link>
        ) : null
      }
      filters={
        <FilterBar
          primary={
            <PositionFilter
              options={PROJECTION_POSITIONS}
              selected={filters.positions}
              onToggle={filters.togglePosition}
            />
          }
          secondary={
            <TeamFilter
              teams={reference.teams}
              selected={knownTeams}
              onToggle={filters.toggleTeam}
            />
          }
          chips={chips}
          onClearAll={clearAll}
        />
      }
    >
      <ToolErrorBoundary what="Projections" onRetry={retry}>
        <React.Suspense fallback={<TableSkeleton columns={SKELETON_COLUMNS} />}>
          <NFLProjectionsTable
            positions={filters.positions}
            teams={knownTeams}
            fetchKey={fetchKey}
            onUpdatedAt={setUpdatedAt}
            notPostedState={notPostedState}
            emptyState={emptyState}
          />
        </React.Suspense>
      </ToolErrorBoundary>
    </NFLToolLayout>
  );
}
